import { SearchLg as SearchIcon } from '@untitledui/icons';
import type {
  FocusEventHandler,
  PointerEventHandler,
  RefAttributes,
  RefObject,
} from 'react';
import { useCallback, useContext, useRef, useState } from 'react';
import type {
  ComboBoxProps as AriaComboBoxProps,
  ComboBoxRenderProps,
  GroupProps as AriaGroupProps,
  ListBoxProps as AriaListBoxProps,
} from 'react-aria-components';
import {
  ComboBox as AriaComboBox,
  ComboBoxStateContext,
  Group as AriaGroup,
  Input as AriaInput,
  ListBox as AriaListBox,
} from 'react-aria-components';

import { useResizeObserver } from '@/hooks/useResizeObserver';
import { HintText } from '@/lib/base/forms/hint-text';
import { Label } from '@/lib/base/forms/input/label';
import {
  type CommonProps,
  SelectContext,
  type SelectItemType,
  type SelectSize,
  sizes,
} from '@/lib/base/forms/select/select-shared';
import { Popover } from '@/lib/base/overlays/popover';
import { cx } from '@/utils/cx';

interface ComboBoxProps
  extends
    Omit<AriaComboBoxProps<SelectItemType>, 'children' | 'items'>,
    RefAttributes<HTMLDivElement>,
    CommonProps {
  shortcut?: boolean;
  items?: SelectItemType[];
  popoverClassName?: string;
  shortcutClassName?: string;
  children: AriaListBoxProps<SelectItemType>['children'];
  icon?: React.ComponentType<{ className?: string }>;
  renderEmptyState?: AriaListBoxProps<SelectItemType>['renderEmptyState'];
}

interface ComboBoxValueProps extends AriaGroupProps {
  size: SelectSize;
  shortcut: boolean;
  placeholder?: string;
  shortcutClassName?: string;
  onFocus?: FocusEventHandler;
  onPointerEnter?: PointerEventHandler;
  ref?: RefObject<HTMLDivElement | null>;
  icon?: React.ComponentType<{ className?: string }>;
  isInvalid?: boolean;
}

const ComboBoxValue = ({
  size,
  shortcut,
  placeholder,
  shortcutClassName,
  icon: Icon = SearchIcon,
  isInvalid,
  ...otherProps
}: ComboBoxValueProps) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ComboBoxStateContext internal type not exported by react-aria-components
  const state = useContext(ComboBoxStateContext) as any;

  const value = state?.selectedItem?.value || null;
  const inputValue = state?.inputValue || null;

  const first = inputValue?.split(value?.supportingText)?.[0] || '';
  const last = inputValue?.split(first)[1];

  return (
    <AriaGroup
      {...otherProps}
      className={({
        isFocusWithin,
        isDisabled,
      }: {
        isFocusWithin: boolean;
        isDisabled: boolean;
      }) =>
        cx(
          'relative flex w-full items-center gap-2 rounded-lg bg-primary shadow-xs ring-1 ring-primary outline-hidden transition-shadow duration-100 ease-linear ring-inset',
          isDisabled && 'cursor-not-allowed bg-disabled_subtle',
          isFocusWithin && !isInvalid && 'ring-2 ring-brand',
          isInvalid && 'ring-2 ring-error',
          sizes[size].root
        )
      }
    >
      {({ isDisabled }: { isDisabled: boolean }) => (
        <>
          <Icon
            className={cx(
              'pointer-events-none shrink-0 text-fg-quaternary',
              sizes[size].icon
            )}
          />

          <div className='relative flex w-full items-center gap-2'>
            {inputValue && (
              <span
                className='absolute top-1/2 z-0 inline-flex w-full -translate-y-1/2 gap-2 truncate'
                aria-hidden='true'
              >
                <p
                  className={cx(
                    // Normal weight — see the note in select.tsx's SelectValue.
                    'text-primary',
                    sizes[size].text,
                    isDisabled && 'text-disabled'
                  )}
                >
                  {first}
                </p>
                {last && (
                  <p
                    className={cx(
                      '-ml-0.75 text-tertiary',
                      sizes[size].text,
                      isDisabled && 'text-disabled'
                    )}
                  >
                    {last}
                  </p>
                )}
              </span>
            )}

            <AriaInput
              placeholder={placeholder}
              className={cx(
                'z-10 w-full appearance-none bg-transparent text-transparent caret-alpha-black/90 placeholder:text-placeholder focus:outline-hidden disabled:cursor-not-allowed disabled:placeholder:text-disabled',
                // The real text is painted by the overlay spans above; this
                // input is transparent. Its font size must still track the
                // scale or the caret and text metrics drift apart.
                sizes[size].text
              )}
            />
          </div>

          {shortcut && (
            <div
              className={cx(
                'absolute inset-y-0.5 right-0.5 z-10 flex items-center rounded-r-[inherit] bg-linear-to-r from-transparent to-bg-primary to-40% pl-8',
                isDisabled && 'to-bg-disabled_subtle',
                sizes[size].shortcut,
                shortcutClassName
              )}
            >
              <span
                className={cx(
                  'pointer-events-none rounded px-1 py-px text-xs font-medium text-quaternary ring-1 ring-secondary select-none ring-inset',
                  isDisabled && 'bg-transparent text-disabled'
                )}
                aria-hidden='true'
              >
                ⌘K
              </span>
            </div>
          )}
        </>
      )}
    </AriaGroup>
  );
};

export const ComboBox = ({
  placeholder = 'Search',
  shortcut = true,
  size = 'sm',
  children,
  items,
  shortcutClassName,
  icon,
  renderEmptyState,
  ...otherProps
}: ComboBoxProps) => {
  const placeholderRef = useRef<HTMLDivElement>(null);
  const [popoverWidth, setPopoverWidth] = useState('');

  // Resize observer for popover width
  const onResize = useCallback(() => {
    if (!placeholderRef.current) {
      return;
    }

    const divRect = placeholderRef.current?.getBoundingClientRect();

    setPopoverWidth(divRect.width + 'px');
  }, [placeholderRef, setPopoverWidth]);

  useResizeObserver({
    ref: placeholderRef,
    box: 'border-box',
    onResize,
  });

  return (
    <SelectContext.Provider value={{ size }}>
      <AriaComboBox
        menuTrigger='focus'
        aria-label={
          typeof otherProps.label === 'string' ? otherProps.label : undefined
        }
        {...otherProps}
      >
        {(state: ComboBoxRenderProps) => (
          <div className='flex flex-col gap-1.5'>
            {otherProps.label && (
              <Label isRequired={state.isRequired} tooltip={otherProps.tooltip}>
                {otherProps.label}
              </Label>
            )}

            <ComboBoxValue
              ref={placeholderRef}
              placeholder={placeholder}
              shortcut={shortcut}
              shortcutClassName={shortcutClassName}
              size={size}
              icon={icon}
              isInvalid={state.isInvalid}
              // This is a workaround to correctly calculating the trigger width
              // while using ResizeObserver wasn't 100% reliable.
              onFocus={onResize}
              onPointerEnter={onResize}
            />

            <Popover
              size={size}
              triggerRef={placeholderRef}
              style={{ width: popoverWidth }}
              className={otherProps.popoverClassName}
            >
              <AriaListBox
                items={items}
                className='size-full outline-hidden'
                renderEmptyState={renderEmptyState}
              >
                {children}
              </AriaListBox>
            </Popover>

            {otherProps.hint && (
              <HintText isInvalid={state.isInvalid}>{otherProps.hint}</HintText>
            )}
          </div>
        )}
      </AriaComboBox>
    </SelectContext.Provider>
  );
};
